import React, { useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import clsx from 'clsx';
import { loadHereMaps } from '@components/features/HereMap/loadHereMaps.ts';
import { calculateTruckRoute, createHereMap, createHerePlatform } from '@components/features/HereMap/hereMapPlatform.ts';
import { decode } from '@components/features/HereMap/hereFlexPolyLine.ts';
import MapUnavailable from '@components/features/HereMap/MapUnavailable.tsx';
import { createLogger } from '@utils/logger';

const log = createLogger('routesMap');

export interface MapRoute {
  id: string | number;
  /** A colour from `CATEGORICAL_COLORS`; the pin labels carry the identity too, so colour is never alone. */
  color: string;
  /** In driving order. */
  stops: { lat: number; lng: number; label: string }[];
}

interface ComponentProps {
  /**
   * Each joined by the road route a truck would drive (HERE routing, as on the expedition map), or by a
   * dashed straight line when no route can be had.
   */
  routes: MapRoute[];
  /** Drawn on top and thicker while the others fade, and the map zooms to it; none = all routes alike. */
  highlightedId?: MapRoute['id'] | null;
  /** A route's line or pin was clicked; `null` for a click on the map itself. */
  onRouteClick?: (id: MapRoute['id'] | null) => void;
  className?: string;
  dataTestId?: string;
}

const PIN_WIDTH = 32;
const PIN_HEIGHT = 40;
/** How much of a faded route is left while another one is highlighted. */
const FADED_OPACITY = 0.3;

/**
 * HERE draws pins and lines from plain values, where CSS variables do not resolve — so theme colours
 * are read from the document and written in.
 */
const themeColor = (variable: string, fallback: string) => getComputedStyle(document.documentElement).getPropertyValue(variable).trim() || fallback;

/** `#rrggbb` at an opacity, as HERE line styles take it. */
const withOpacity = (hex: string, opacity: number) => {
  const value = parseInt(hex.replace('#', ''), 16);
  return Number.isNaN(value) ? hex : `rgba(${(value >> 16) & 255}, ${(value >> 8) & 255}, ${value & 255}, ${opacity})`;
};

/** Pin in the route's colour; the label sits on a white disc in text ink, readable whatever the route colour. */
const pinSvg = (label: string, color: string, surface: string, ink: string, opacity: number) =>
  `<svg xmlns="http://www.w3.org/2000/svg" width="${PIN_WIDTH}" height="${PIN_HEIGHT}" viewBox="0 0 32 40" opacity="${opacity}">
    <path d="M16 1C7.7 1 2 7.2 2 15c0 10.4 14 24 14 24s14-13.6 14-24C30 7.2 24.3 1 16 1z" fill="${color}" stroke="${surface}" stroke-width="2"/>
    <circle cx="16" cy="15" r="10" fill="${surface}"/>
    <text x="16" y="19" font-family="DM Sans, sans-serif" font-size="${label.length > 2 ? 9 : 11}" font-weight="700" text-anchor="middle" fill="${ink}">${label}</text>
  </svg>`;

/** What is drawn for one route, kept so a highlight can restyle it without fetching the road again. */
interface DrawnRoute {
  route: MapRoute;
  group: any;
  lines: { casing: any; line: any; dashed: boolean }[];
  markers: { marker: any; label: string }[];
}

const RingilRoutesMapInner: React.FC<ComponentProps> = ({ routes, highlightedId = null, onRouteClick, className, dataTestId }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<any>(null);
  const [unavailable, setUnavailable] = useState(false);
  const platformRef = useRef<any>(null);
  // Cleared just before the map is disposed: road routes and resizes still arriving after that (the
  // section folded right after opening) must not touch it, or HERE throws from inside.
  const aliveRef = useRef(false);
  const drawnRef = useRef<{ all: any; routes: Map<MapRoute['id'], DrawnRoute> } | null>(null);
  // Read by HERE's listeners and the resize observer, which are set up once and would otherwise see stale props.
  const highlightedRef = useRef(highlightedId);
  highlightedRef.current = highlightedId;
  const onRouteClickRef = useRef(onRouteClick);
  onRouteClickRef.current = onRouteClick;

  /**
   * Shows the highlighted route, or all of them, road lines included. Fitting is only right for the size
   * the map has at that moment, so it runs again whenever the container is resized — including the first
   * layout after the map is created — each time a road route arrives, and when the highlight changes.
   */
  const fit = (target: any) => {
    const drawn = drawnRef.current;
    if (!aliveRef.current || !target || !drawn) return;
    target.getViewPort().resize();
    const bounds = (drawn.routes.get(highlightedRef.current)?.group ?? drawn.all).getBoundingBox();
    if (!bounds) return;
    // A single pin has a zero-size box; centre it at a readable zoom instead.
    if (bounds.getTop() === bounds.getBottom() && bounds.getLeft() === bounds.getRight()) {
      target.getViewModel().setLookAtData({ position: bounds.getCenter(), zoom: 11 });
    } else {
      target.getViewModel().setLookAtData({ bounds });
    }
  };

  /** Highlighted route on top and thicker, the others faded; all alike when nothing is highlighted. */
  const applyHighlight = () => {
    const drawn = drawnRef.current;
    if (!aliveRef.current || !drawn) return;
    const surface = themeColor('--ringil3-base-secondary-color', 'white');
    const ink = themeColor('--ringil3-content-primary', 'black');
    // An id that is not on the map (e.g. after the routes changed) highlights nothing rather than fading everything.
    const highlighted = drawn.routes.has(highlightedRef.current) ? highlightedRef.current : null;

    drawn.routes.forEach(({ route, group, lines, markers }) => {
      const isHighlighted = highlighted === route.id;
      const faded = highlighted !== null && !isHighlighted;
      group.setZIndex(isHighlighted ? 1 : 0);
      lines.forEach(({ casing, line, dashed }) => {
        casing.setStyle({ lineWidth: isHighlighted ? 10 : 7, strokeColor: faded ? withOpacity(surface, FADED_OPACITY) : surface });
        line.setStyle({
          lineWidth: (isHighlighted ? 6 : 4) - (dashed ? 1 : 0),
          strokeColor: faded ? withOpacity(route.color, FADED_OPACITY) : route.color,
          ...(dashed ? { lineDash: [4, 4] } : {}),
        });
      });
      markers.forEach(({ marker, label }) =>
        marker.setIcon(
          new (window as any).H.map.Icon(pinSvg(label, route.color, surface, ink, faded ? FADED_OPACITY : 1), { anchor: { x: PIN_WIDTH / 2, y: PIN_HEIGHT } }),
        ),
      );
    });
  };

  useEffect(() => {
    let created: any = null;
    let cancelled = false;
    // HERE settles its first view asynchronously; disposing before that makes it throw from inside its
    // render loop (folding the map right after opening it). So disposal waits for that first settle.
    let settled = false;
    let disposed = false;
    const dispose = () => {
      if (disposed || !created) return;
      disposed = true;
      created.dispose();
    };

    loadHereMaps()
      .then(H => {
        if (cancelled || !containerRef.current) return;
        platformRef.current = createHerePlatform(H);
        created = createHereMap(H, containerRef.current, platformRef.current);
        // Room around the routes so the pins at their ends are not cut off; the top is pin height plus a margin.
        created.getViewPort().setPadding(PIN_HEIGHT + 16, 32, 32, 32);
        // A click on the map itself, not on a route, clears the highlight; route objects report their own.
        aliveRef.current = true;
        created.addEventListener('mapviewchangeend', () => {
          settled = true;
          if (cancelled) dispose();
        });
        created.addEventListener('tap', (event: any) => event.target === created && onRouteClickRef.current?.(null));
        setMap(created);
      })
      .catch(() => !cancelled && setUnavailable(true));

    const observer = new ResizeObserver(() => fit(created));
    if (containerRef.current) observer.observe(containerRef.current);

    return () => {
      cancelled = true;
      aliveRef.current = false;
      observer.disconnect();
      if (settled) dispose();
      // Should the first settle never come, the map is still released.
      else setTimeout(dispose, 5000);
    };
  }, []);

  useEffect(() => {
    const H = (window as any).H;
    if (!map || !H) return;

    map.removeObjects(map.getObjects());
    drawnRef.current = null;
    if (routes.length === 0) return;

    let stale = false;
    const all = new H.map.Group();
    const drawnRoutes = new Map<MapRoute['id'], DrawnRoute>();

    routes.forEach(route => {
      const group = new H.map.Group();
      const drawnRoute: DrawnRoute = { route, group, lines: [], markers: [] };
      drawnRoutes.set(route.id, drawnRoute);
      all.addObject(group);
      // Lines and pins of one route react to a click as one.
      group.addEventListener('tap', () => onRouteClickRef.current?.(route.id));

      // Lines below the pins; each over a casing in the surface colour, so a route running along another
      // one stays visible as a separate line. Styled by applyHighlight.
      const addLine = (points: { lat: number; lng: number }[], dashed: boolean) => {
        const geometry = new H.geo.LineString();
        points.forEach(point => geometry.pushPoint(point));
        const casing = new H.map.Polyline(geometry, { zIndex: 0 });
        const line = new H.map.Polyline(geometry, { zIndex: 1 });
        group.addObjects([casing, line]);
        drawnRoute.lines.push({ casing, line, dashed });
      };

      route.stops.forEach(stop => {
        // Anchored at the tip, so the pin points at the place rather than covering it with its corner.
        const marker = new H.map.Marker({ lat: stop.lat, lng: stop.lng }, { zIndex: 2, icon: new H.map.Icon('<svg xmlns="http://www.w3.org/2000/svg"/>') });
        group.addObject(marker);
        drawnRoute.markers.push({ marker, label: stop.label });
      });

      if (route.stops.length < 2) return;
      calculateTruckRoute(platformRef.current, route.stops)
        .then(polylines => {
          if (stale) return;
          polylines.forEach(polyline =>
            addLine(
              decode(polyline).polyline.map(([lat, lng]: number[]) => ({ lat, lng })),
              false,
            ),
          );
        })
        .catch(error => {
          if (stale) return;
          // Routing can be refused (key without routing, a stop no truck can reach); the order is still worth showing.
          log.error(`No road route for route ${route.id}, showing straight lines`, error);
          addLine(route.stops, true);
        })
        .finally(() => {
          if (stale) return;
          applyHighlight();
          fit(map);
        });
    });

    map.addObject(all);
    drawnRef.current = { all, routes: drawnRoutes };
    applyHighlight();
    fit(map);

    return () => {
      stale = true;
    };
  }, [map, routes]);

  useEffect(() => {
    applyHighlight();
    fit(map);
  }, [highlightedId, map]);

  if (unavailable) return <MapUnavailable />;

  return <div ref={containerRef} className={clsx('relative overflow-hidden rounded-2xl', className)} data-testid={dataTestId} />;
};

/** A map is non-essential: any failure inside it shows the placeholder instead of taking the page down. */
const RingilRoutesMap: React.FC<ComponentProps> = props => (
  <Sentry.ErrorBoundary fallback={<MapUnavailable />}>
    <RingilRoutesMapInner {...props} />
  </Sentry.ErrorBoundary>
);

export default RingilRoutesMap;
